import HoverBoxAnchor from "../../UI/hoverBox/HoverBoxAnchor";
import LabeledSection from "../../UI/LabeledSection";
import { ReactComponent as InfoIcon } from "../../../assets/icons/Info.svg";
import labels from "../../../utils/labels";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import CalendarSettingsService from "../../../service/calendarSettingsService";
import TimePicker from "../../UI/input/TimePicker";
import {
  CalendarTimeWindow,
  TimeFormat,
} from "../../../types/calendarSettings";
import { useEffect, useRef, useState } from "react";
import timestampService from "../../../service/timestampService";
import { useQueryParams } from "../../../hooks/useQueryParams";

const WorkTimeSettings = () => {
  const timeWindow = useAppSelector(
    (state) => state.calendarSettingsSlice.calendarSettingsData?.timeWindow,
  );
  const { activeNexusFilters } = useAppSelector(
    (state) => state.nexusFiltersSlice,
  );
  const dispatch = useAppDispatch();
  const queryKey = useQueryParams();
  const filterId = activeNexusFilters?.find(
    (x) => x.id === queryKey.filterId,
  )?.filterId;

  const [localTime, setLocalTime] = useState<CalendarTimeWindow>({
    startTime: "None",
    endTime: "None",
  });
  const initialTimeRef = useRef<CalendarTimeWindow>({
    startTime: "None",
    endTime: "None",
  });

  const getTimeOrDefault = (value: string | null, fallback: TimeFormat) => {
    return !value || value.trim() === "" ? fallback : (value as TimeFormat);
  };

  useEffect(() => {
    if (!timeWindow) return;
    const mappedTime: CalendarTimeWindow = {
      startTime: getTimeOrDefault(timeWindow.startTime, "00:00:00"),
      endTime: getTimeOrDefault(timeWindow.endTime, "23:59:59"),
    };
    setLocalTime(mappedTime);
    initialTimeRef.current = mappedTime;
  }, [timeWindow]);

  async function handleTimeUpdate(time: "start" | "end", value: TimeFormat) {
    if (!filterId) return;
    const newTime: CalendarTimeWindow = {
      startTime: time === "start" ? value : localTime.startTime,
      endTime: time === "end" ? value : localTime.endTime,
    };
    setLocalTime(newTime);

    try {
      await CalendarSettingsService.editCalendarTimeWindow(
        filterId,
        newTime.startTime,
        newTime.endTime,
        dispatch,
      );
      initialTimeRef.current = newTime;
    } catch {
      setLocalTime(initialTimeRef.current);
    }
  }

  return (
    <LabeledSection
      classes="flex-1"
      title={labels.workTimeSettings}
      spacing="big"
      icon={
        <HoverBoxAnchor tooltipText={labels.workTimeSettingsInfo}>
          <InfoIcon width={16} height={16} />
        </HoverBoxAnchor>
      }
    >
      <div className="flex gap-4">
        <TimePicker
          id="start-time"
          value={timestampService.timeToMinutes(localTime.startTime)}
          onTimeChange={(newStart) =>
            handleTimeUpdate("start", timestampService.timeToSeconds(newStart))
          }
          label={labels.startTime}
          classes="w-full"
        />
        <TimePicker
          id="end-time"
          value={timestampService.timeToMinutes(localTime.endTime)}
          onTimeChange={(newEnd) =>
            handleTimeUpdate("end", timestampService.timeToSeconds(newEnd))
          }
          label={labels.endTime}
          classes="w-full"
        />
      </div>
    </LabeledSection>
  );
};

export default WorkTimeSettings;
